import type { PeriodeLektioner } from "@/interfaces/efteruddannelse";
import type { AfgorelseDetailModel } from "@/interfaces/afgorelse";
import {
  calculateReportCategorySummaries,
  MAX_TOTAL_TRANSFER_LESSONS,
} from "./calculateReportCategorySummaries";

export type InjunctionCategoryKey =
  "courseCertificates" | "teaching" | "authorship" | "reductions" | "legalAid";

export type InjunctionCategoryConfig = {
  key: InjunctionCategoryKey;
  label: string;
  canTransferLessons: boolean;
  /** Kategoriloft for det indberettede. Udeladt = ingen grænse (kursusdeltagelse). */
  maxReported?: number;
  maxTransfer: number;
};

export const INJUNCTION_CATEGORY_CONFIG: InjunctionCategoryConfig[] = [
  {
    key: "courseCertificates",
    label: "Kursusdeltagelse",
    canTransferLessons: true,
    // Ingen maxReported: reglerne sætter ingen grænse for kursuslektioner.
    maxTransfer: 54,
  },
  {
    key: "teaching",
    label: "Undervisning",
    maxReported: 27,
    canTransferLessons: true,
    maxTransfer: 27,
  },
  {
    key: "authorship",
    label: "Forfatterskab",
    maxReported: 18,
    canTransferLessons: true,
    maxTransfer: 18,
  },
  {
    key: "reductions",
    label: "Reduktion",
    maxReported: 54,
    canTransferLessons: false,
    maxTransfer: 54,
  },
  {
    key: "legalAid",
    label: "Advokatvagt/retshjælp",
    maxReported: 18,
    canTransferLessons: false,
    maxTransfer: 18,
  },
];

const SUBMITTED_OVERVIEW_CATEGORY_KEYS = [
  { key: "kursus", label: "Kursusdeltagelse", canTransferLessons: true },
  { key: "undervisning", label: "Undervisning", canTransferLessons: true },
  { key: "forfatter", label: "Forfatterskab", canTransferLessons: true },
  { key: "reduktion", label: "Reduktion", canTransferLessons: false },
  {
    key: "advokatvagt",
    label: "Advokatvagt/retshjælp",
    canTransferLessons: false,
  },
] as const;

export type InjunctionCategorySummary = {
  key: InjunctionCategoryKey;
  label: string;
  maxReported?: number;
  maxTransfer: number;
  canTransferLessons: boolean;
  selectedLessons: number;
  totalLessons: number;
  submittedLessons: number;
  transferredToNext: number;
  /** Lektioner over kategoriloftet, som hverken indberettes eller overføres. */
  droppedLessons: number;
};

export type InjunctionReportOverviewRow = {
  key: string;
  label: string;
  totalLessons?: number;
  submittedLessons: number;
  transferredToNext: number;
  /**
   * Kun sat på kladde-rækker. Den indsendte visning kender hverken kategoriloftet
   * eller det bortfaldne overskud, så begge felter er valgfri.
   */
  maxReported?: number;
  droppedLessons?: number;
  /** Udeladt på indsendte rækker; de har intet bortfald at forklare. */
  canTransferLessons?: boolean;
};

const getSummaryByKey = (
  summaries: InjunctionCategorySummary[],
  key: InjunctionCategoryKey,
): InjunctionCategorySummary | undefined =>
  summaries.find((summary) => summary.key === key);

export const calculateInjunctionCategorySummaries = (
  selectedLessonsByCategory: Record<InjunctionCategoryKey, number>,
  requiredLessons: number,
): InjunctionCategorySummary[] => {
  const summaries = calculateReportCategorySummaries(
    INJUNCTION_CATEGORY_CONFIG.map((category) => ({
      key: category.key,
      canTransferLessons: category.canTransferLessons,
      maxReported: category.maxReported,
      maxTransfer: category.maxTransfer,
      selectedLessons: selectedLessonsByCategory[category.key] ?? 0,
    })),
    requiredLessons,
    // Afgørelser kører præcis samme regler som efteruddannelsesperioden — kun tærsklen
    // er en anden: afgørelsens egen paakraevedeLektioner i stedet for periodens 54.
    // Overførsel kræver derfor også her, at kravet er nået.
    //
    // maxTotalTransfer bliver ved med at være 54: overskuddet havner i medlemmets
    // AKTUELLE efteruddannelsesperiode, og den kan rumme 54, uanset hvor stort påbuddet var.
    //
    // NB: selve overførslen sker IKKE i portalen. Signeringen sætter kun indberetningen til
    // Indsendt; det er AIKA, der på sync-back udpeger målperioden og sender den tilbage som
    // PaabudIndberetningAIKAModel.OverfoertTilEfteruddannelsesperiodeId — det felt er det
    // eneste, UpsertPaabudIndberetninger skriver, og portalen læser det i
    // EfteruddannelseLogic.HentPaabudOverflowForPeriodeAsync. Der findes altså ingen metode
    // i backenden at slå op; led ikke efter en.
    {
      requireFullReportBeforeTransfer: true,
      maxTotalTransfer: MAX_TOTAL_TRANSFER_LESSONS,
    },
  );

  return summaries.map((summary, index) => ({
    key: summary.key,
    label: INJUNCTION_CATEGORY_CONFIG[index].label,
    maxReported: INJUNCTION_CATEGORY_CONFIG[index].maxReported,
    maxTransfer: INJUNCTION_CATEGORY_CONFIG[index].maxTransfer,
    canTransferLessons: INJUNCTION_CATEGORY_CONFIG[index].canTransferLessons,
    selectedLessons: summary.selectedLessons,
    totalLessons: summary.totalLessons,
    submittedLessons: summary.submittedLessons,
    transferredToNext: summary.transferredToNext,
    droppedLessons: summary.droppedLessons,
  }));
};

const getCategoryLessons = (
  summaries: InjunctionCategorySummary[],
  key: InjunctionCategoryKey,
  getLessons: (summary: InjunctionCategorySummary) => number,
): number => {
  const summary = getSummaryByKey(summaries, key);

  return summary ? getLessons(summary) : 0;
};

export const mapInjunctionSummariesToPeriodeLektioner = (
  summaries: InjunctionCategorySummary[],
  getLessons: (summary: InjunctionCategorySummary) => number,
): PeriodeLektioner => {
  const lessons = {
    advokatvagt: getCategoryLessons(summaries, "legalAid", getLessons),
    dispensation: 0,
    forfatter: getCategoryLessons(summaries, "authorship", getLessons),
    kursus: getCategoryLessons(summaries, "courseCertificates", getLessons),
    reduktion: getCategoryLessons(summaries, "reductions", getLessons),
    undervisning: getCategoryLessons(summaries, "teaching", getLessons),
  };

  return {
    ...lessons,
    sum: Object.values(lessons).reduce((total, lesson) => total + lesson, 0),
  };
};

export const toPendingInjunctionOverviewRows = (
  summaries: InjunctionCategorySummary[],
): InjunctionReportOverviewRow[] =>
  summaries.map((summary) => ({
    key: summary.key,
    label: summary.label,
    totalLessons: summary.totalLessons,
    submittedLessons: summary.submittedLessons,
    transferredToNext: summary.transferredToNext,
    maxReported: summary.maxReported,
    droppedLessons: summary.droppedLessons,
    canTransferLessons: summary.canTransferLessons,
  }));

export const toSubmittedInjunctionOverviewRows = (
  afgorelse: AfgorelseDetailModel,
): InjunctionReportOverviewRow[] =>
  SUBMITTED_OVERVIEW_CATEGORY_KEYS.map(
    ({ key, label, canTransferLessons }) => ({
      key,
      label,
      canTransferLessons,
      submittedLessons: afgorelse.lektionerIndberettet?.[key] ?? 0,
      transferredToNext: afgorelse.lektionerOverfoeresTilNaeste?.[key] ?? 0,
    }),
  );
